/* eslint-disable react/prop-types */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    StepCompletedProfiles,
    StepEmployeeStudentAssociation,
    StepExternalRecipients,
    StepFormEditor,
    StepRecipientAssociation,
    StepRecipientBothParents,
    StepTemplateSelection,
    StepTriggerConditions,
} from './CreateFormWizardSteps';
import FormPostCreateSetup from '../FormPostCreateSetup';
import FormExpirationDatePicker from '../FormExpirationDatePicker';
import CircleIcon from '@mui/icons-material/Circle';
import CancelIcon from '@mui/icons-material/Close';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {
    useCreateExternalUserFormAssignmentsMutation,
    useCreateFormEmployeeAssignmentMutation,
    useCreateFormQuestionAssignmentMutation,
    useCreateFormStudentAssignmentMutation,
} from '../../../../api/services/formAssignmentsService';
import {
    useCreateFormMutation,
    useGetFormTemplatesQuery,
    useUpdateFormMutation,
} from '../../../../api/services/formsService';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import FORM_TYPE, {
    FORM_TYPE_OPTIONS,
    SETTINGS_FORM_TYPE_OPTIONS,
    getFormBehaviorFromType,
} from '../../../../constants/FormType';
import {
    PARENT_AUDIENCE_TYPE,
    getParentAudienceTypeForSelection,
    resolveBackendAudiencePayload,
} from '../../../../constants/ParentAudienceType';
import useNotification from '../../../../utility/hooks/useNotification';
import useUser from '../../../../utility/hooks/useUser';
import {validateFieldLength} from '../../../../utility/validationUtil';
import EmailsInput from '../../../../shared-components/EmailsInput';
import SettingsSectionBase from '../PrivateFormSettings/SettingsSection';
import moment from 'moment';

const FORM_NAME_MAX_LENGTH = 128;

const SETTINGS_SIDE_CLEARANCE = 'clamp(160px, 16vw, 240px)';
const SETTINGS_CONTENT_MAX_WIDTH = '1800px';
const FOOTER_ACTION_BUTTON_WIDTH = 165;
const FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE = {xs: 175, sm: FOOTER_ACTION_BUTTON_WIDTH};
const SECTION_SX = {
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    backgroundColor: '#ffffff',
    boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.05)',
};

const LINKED_EMPLOYEE_AUDIENCE = `${FORM_AUDIENCE_TYPE.employees}|linked`;
const PARENT_WITH_ACCOUNT_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|with-account`;
const PARENT_WITHOUT_ACCOUNT_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|without-account`;
const PARENT_APPLICANT_STATUS_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|applicant-status`;

const CREATE_FORM_TYPE_OPTIONS = [
    ...SETTINGS_FORM_TYPE_OPTIONS,
    ...FORM_TYPE_OPTIONS.filter(({id}) =>
        [
            FORM_TYPE.application,
            FORM_TYPE.shareable,
        ].includes(id)
    )
];

const getFormTypeValueForWizard = (selectedType) =>
    selectedType === FORM_TYPE.emailCommunication
        ? FORM_TYPE.email
        : selectedType;

const ProgressiveSection = ({
    question,
    header,
    headerIcon,
    subHeader,
    children
}) => (
    <Stack spacing={1.5}>
        <Typography
            variant="h6"
            color="text.primary"
            sx={{
                fontWeight: 600,
                lineHeight: 1.35,
            }}
        >
            {question}
        </Typography>
        <Box sx={SECTION_SX}>
            <SettingsSectionBase
                header={header}
                headerIcon={headerIcon}
                subHeader={subHeader}
            >
                {children}
            </SettingsSectionBase>
        </Box>
    </Stack>
);

const CreateFormWizard = () => {
    const {user} = useUser();
    const {
        showSuccessNotification,
        showErrorNotification,
    } = useNotification();
    const [isCreatingForm, setIsCreatingForm] = useState(false);
    const [selectedFormType, setSelectedFormType] = useState('');
    const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
    const [stage, setStage] = useState('setup');
    const [createdFormId, setCreatedFormId] = useState(null);
    const [schemaValidation, setSchemaValidation] = useState({});
    const [componentToEditIndex, setComponentToEditIndex] = useState(undefined);
    const [isBuilderAtBottom, setIsBuilderAtBottom] = useState(false);
    const [isTemplateSelectionLocked, setIsTemplateSelectionLocked] = useState(false);
    const builderScrollContainerRef = useRef(null);
    const builderBottomSentinelRef = useRef(null);
    const isSmallScreen = useMediaQuery('(max-width:1100px)');

    const schoolLogoSchemaComponent = useMemo(
        () => ({
            type: 'schoolLogo',
            url: user?.schoolLogoUrl
        }),
        [user?.schoolLogoUrl]
    );

    const initialState = useMemo(() => ({
        form: {
            type: null,
            audience: null,
            selection: '',
        },
        template: {
            creationMethod: 'template',
            templateId: null,
            templateObject: null,
            categoryFilter: null,
            typeFilter: null,
        },
        details: {
            formName: '',
            expiryDate: '',
            goLiveDate: '',
            reminderFrequency: 0,
            allParentsCanSubmit: false,
            notificationEmail: [],
            categories: [],
            accessLevel: null,
            parentAccountType: null,
            strictRecipientsFiltering: false,
            allowMultipleSubmissions: false,
            enableRecipientNotifications: false,
            enableAutoresponder: false,
            isCommunication: false,
            profileMustBeComplete: false,
            linkToStudents: false,
            linkedStrictFiltering: false,
            allLinkedEmployeesCanSubmit: false,
        },
        recipients: {
            assignmentType: null,
            student: {
                students: [],
                applications: [],
                enquiries: [],
            },
            employees: [],
            groups: [],
            externalEmails: [],
        },
        associations: {
            students: [],
            applications: [],
            enquiries: [],
            groups: []
        },
        trigger: {
            selectedForm: null,
            selectedFormSchema: null,
            relatedComponent: null,
            targetState: null,
            relatedFormEmailFieldId: null,
            relatedFormEmployeeFieldId: null,
            externalEmails: [],
        },
        schema: []
    }), []);

    const [wizard, setWizard] = useState(initialState);

    const [createForm] = useCreateFormMutation();
    const [updateForm] = useUpdateFormMutation();
    const [createFormQuestionAssignment] = useCreateFormQuestionAssignmentMutation();
    const [createFormStudentAssignments] = useCreateFormStudentAssignmentMutation();
    const [createFormEmployeeAssignment] = useCreateFormEmployeeAssignmentMutation();
    const [createExternalUserFormAssignments] = useCreateExternalUserFormAssignmentsMutation();
    const {data: allTemplates = []} = useGetFormTemplatesQuery();
    const {data: formCategories = []} = useGetFormCategoriesQuery();
    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: false});

    const computeAccessLevel = (state) => {
        const templateType = state.template.templateObject?.type;
        const isParent = state.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;

        if (
            state.form.audience === FORM_AUDIENCE_TYPE.externalUsers ||
            state.form.audience === FORM_AUDIENCE_TYPE.enquiries
        ) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (isParent && !['with', 'with-account'].includes(state.details.parentAccountType)) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (templateType === 'transition' || templateType === 'offer') {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (['acceptance', 'application', 'shareable'].includes(templateType)) {
            return FORM_ACCESS_LEVELS.public;
        }

        return FORM_ACCESS_LEVELS.private;
    };

    const accessLevel = useMemo(
        () => computeAccessLevel(wizard),
        [
            wizard.details.parentAccountType,
            wizard.form.audience,
            wizard.template.templateObject?.type
        ]
    );

    useEffect(() => {
        setWizard(prev => {
            if (prev.details.accessLevel === accessLevel) {
                return prev;
            }

            return {
                ...prev,
                details: {
                    ...prev.details,
                    accessLevel
                }
            };
        });
    }, [accessLevel]);

    useEffect(() => {
        if (!wizard.template.templateObject) {
            return;
        }

        setWizard(prev => ({
            ...prev,
            schema: [
                ...(wizard.template.templateObject.schema ?? []),
                schoolLogoSchemaComponent
            ]
        }));
    }, [
        wizard.template.templateId,
        wizard.template.templateObject,
        schoolLogoSchemaComponent
    ]);

    useEffect(() => {
        if (!allTemplates.length) {
            return;
        }

        if (wizard.template.creationMethod !== 'blank') {
            return;
        }

        if (wizard.form.type === FORM_TYPE.shareable) {
            if (wizard.template.templateId) {
                return;
            }

            const blankShareable =
                allTemplates.find(template =>
                    template.category === 'Blank' &&
                    template.type === 'shareable' &&
                    !template.schoolId
                ) ||
                allTemplates.find(template =>
                    template.category === 'Blank' &&
                    template.type === 'shareable'
                );

            if (!blankShareable) {
                return;
            }

            setWizard(prev => ({
                ...prev,
                template: {
                    ...prev.template,
                    templateId: blankShareable.id,
                    templateObject: blankShareable,
                    categoryFilter: blankShareable.category,
                    typeFilter: blankShareable.type,
                },
                schema: [...(blankShareable.schema ?? []), schoolLogoSchemaComponent],
            }));

            return;
        }

        if (wizard.form.audience == null) {
            return;
        }

        let requiredType = 'regular';

        if (wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            requiredType =
                ['without', 'applicant-status'].includes(wizard.details.parentAccountType)
                    ? 'offer'
                    : 'regular';
        } else if (wizard.form.audience === FORM_AUDIENCE_TYPE.previousSchool) {
            requiredType = 'transition';
        }

        const targetBlank =
            allTemplates.find(template =>
                template.category === 'Blank' &&
                template.type === requiredType &&
                !template.schoolId
            ) ||
            allTemplates.find(template =>
                template.category === 'Blank' &&
                template.type === 'regular' &&
                !template.schoolId
            );

        if (!targetBlank) {
            return;
        }

        setWizard(prev => ({
            ...prev,
            template: {
                ...prev.template,
                templateId: targetBlank.id,
                templateObject: targetBlank,
                categoryFilter: 'Blank',
                typeFilter: requiredType
            },
            schema: [...(targetBlank.schema ?? []), schoolLogoSchemaComponent]
        }));
    }, [
        allTemplates,
        schoolLogoSchemaComponent,
        wizard.details.isCommunication,
        wizard.details.parentAccountType,
        wizard.form.audience,
        wizard.form.type,
        wizard.template.creationMethod,
        wizard.template.templateId
    ]);

    const audienceOptions = useMemo(() => {
        const baseOptions = [
            {
                value: PARENT_WITH_ACCOUNT_AUDIENCE,
                label: 'Parent with an Admicity account'
            },
            {
                value: PARENT_WITHOUT_ACCOUNT_AUDIENCE,
                label: 'Parent without an Admicity account'
            },
            {
                value: PARENT_APPLICANT_STATUS_AUDIENCE,
                label: 'Parent of applicant with Applied or Offered status'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.previousSchool}`,
                label: 'Child\'s previous school'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.employees}`,
                label: 'Employee'
            },
            {
                value: LINKED_EMPLOYEE_AUDIENCE,
                label: 'Employee linked to student'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.externalUsers}`,
                label: 'External recipient'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.students}`,
                label: 'Student'
            },
            ...(schoolInfo && schoolInfo.disableEnquiriesSync === false
                ? [{
                    value: `${FORM_AUDIENCE_TYPE.enquiries}`,
                    label: 'Enquiry'
                }]
                : [])
        ];

        if (selectedFormType === FORM_TYPE.selfService) {
            return baseOptions.filter((option) => {
                const [audienceId, optionType] = String(option.value).split('|');
                const numericAudience = Number(audienceId);

                return (
                    numericAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    (
                        numericAudience === FORM_AUDIENCE_TYPE.employees &&
                        optionType !== 'linked'
                    )
                );
            });
        }

        if (selectedFormType === FORM_TYPE.triggerForm) {
            return baseOptions.filter(
                option => option.value !== `${FORM_AUDIENCE_TYPE.students}`
            );
        }

        if (selectedFormType === FORM_TYPE.emailCommunication) {
            return baseOptions.filter(option => option.value !== LINKED_EMPLOYEE_AUDIENCE);
        }

        return baseOptions;
    }, [schoolInfo, selectedFormType]);

    const handleFormTypeChange = (event) => {
        const nextSelectedType = event.target.value;
        const nextFormType = getFormTypeValueForWizard(nextSelectedType);
        const nextIsCommunication = nextSelectedType === FORM_TYPE.emailCommunication;
        const nextBehaviour = getFormBehaviorFromType(nextFormType);

        setSelectedFormType(nextSelectedType);
        setIsTemplateDialogOpen(false);
        setWizard({
            ...initialState,
            form: {
                ...initialState.form,
                type: nextFormType,
            },
            template: {
                ...initialState.template,
                creationMethod: nextIsCommunication ? 'blank' : 'template',
            },
            details: {
                ...initialState.details,
                allowMultipleSubmissions: nextIsCommunication
                    ? false
                    : nextBehaviour.allowMultipleSubmissions,
                enableRecipientNotifications: nextIsCommunication
                    ? true
                    : nextBehaviour.enableRecipientNotifications,
                isCommunication: nextIsCommunication,
            }
        });
    };

    const handleAudienceChange = (event) => {
        const selectedAudienceValue = event.target.value;
        const isLinkedEmployeeAudience = selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
        const [audienceId, optionType] = String(selectedAudienceValue).split('|');
        const numericAudience = isLinkedEmployeeAudience
            ? FORM_AUDIENCE_TYPE.employees
            : Number(audienceId);
        const parentAccountType =
            numericAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? (optionType || null)
                : null;

        setIsTemplateDialogOpen(false);
        setWizard(prev => ({
            ...prev,
            form: {
                ...prev.form,
                selection: selectedAudienceValue,
                audience: numericAudience,
            },
            template: {
                ...initialState.template,
                creationMethod: prev.details.isCommunication ? 'blank' : 'template',
            },
            recipients: initialState.recipients,
            trigger: initialState.trigger,
            associations: {
                ...initialState.associations,
            },
            schema: [],
            details: {
                ...prev.details,
                parentAccountType,
                linkToStudents: isLinkedEmployeeAudience,
                allLinkedEmployeesCanSubmit: false,
                linkedStrictFiltering: false,
            }
        }));
    };

    const handleBuildMethodChange = (event) => {
        const nextCreationMethod = event.target.value;

        setWizard(prev => ({
            ...prev,
            template: {
                ...initialState.template,
                creationMethod: nextCreationMethod,
            },
            schema: [],
            details: {
                ...prev.details,
                formName: '',
                expiryDate: '',
                goLiveDate: '',
                reminderFrequency: 0,
                notificationEmail: [],
                categories: [],
                enableAutoresponder: false,
            }
        }));

        if (nextCreationMethod === 'template' || nextCreationMethod === 'schoolTemplate') {
            setIsTemplateDialogOpen(true);
        } else {
            setIsTemplateDialogOpen(false);
        }
    };

    const isTypeSelected = !!selectedFormType;
    const requiresAudience = ![FORM_TYPE.application, FORM_TYPE.shareable].includes(wizard.form.type);
    const hasAudienceSelection = !requiresAudience || wizard.form.audience != null;
    const isTriggerForm = wizard.form.type === FORM_TYPE.triggerForm;
    const isTriggerConfigured =
        !isTriggerForm ||
        (
            !!wizard.trigger.selectedForm?.id &&
            !!wizard.trigger.relatedComponent &&
            !!wizard.trigger.targetState
        );
    const showRecipientSection =
        hasAudienceSelection &&
        !isTriggerForm &&
        [
            FORM_AUDIENCE_TYPE.externalUsers,
            FORM_AUDIENCE_TYPE.parentsForChildCompletion,
            FORM_AUDIENCE_TYPE.enquiries,
            FORM_AUDIENCE_TYPE.students,
            FORM_AUDIENCE_TYPE.previousSchool,
            FORM_AUDIENCE_TYPE.employees
        ].includes(wizard.form.audience);
    const showExternalRecipients =
        showRecipientSection &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers;
    const showLinkedStudents =
        showRecipientSection &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
        wizard.details.linkToStudents;
    const showParentCompletion =
        hasAudienceSelection &&
        wizard.details.isCommunication !== true &&
        wizard.form.type !== FORM_TYPE.selfService &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        ['with', 'with-account'].includes(wizard.details.parentAccountType);

    const isRecipientSectionComplete = !showRecipientSection || true;
    const isExternalRecipientsSectionComplete = !showExternalRecipients || true;
    const isLinkedStudentsSectionComplete = !showLinkedStudents || true;
    const isParentCompletionSectionComplete = !showParentCompletion || true;

    const isPreBuildSectionComplete =
        isTypeSelected &&
        hasAudienceSelection &&
        isTriggerConfigured &&
        isRecipientSectionComplete &&
        isExternalRecipientsSectionComplete &&
        isLinkedStudentsSectionComplete &&
        isParentCompletionSectionComplete;
    const shouldSkipTemplateSelection = wizard.details.isCommunication === true;
    const canShowTemplateSection =
        !shouldSkipTemplateSelection &&
        isPreBuildSectionComplete;

    const isTemplateMode =
        wizard.template.creationMethod === 'template' ||
        wizard.template.creationMethod === 'schoolTemplate';
    const hasAssignedTemplate = !!wizard.template.templateId;
    const isBuildSectionComplete =
        shouldSkipTemplateSelection
            ? hasAssignedTemplate
            : (
                !!wizard.template.creationMethod &&
                hasAssignedTemplate
            );
    const canShowFormDetailsSection =
        isPreBuildSectionComplete &&
        isBuildSectionComplete;
    const formNameValidation = validateFieldLength(wizard.details.formName, FORM_NAME_MAX_LENGTH);

    const canShowSettingsSection =
        canShowFormDetailsSection &&
        formNameValidation.isValid;

    const shouldShowExpirationDate = wizard.form.type !== FORM_TYPE.selfService;
    const shouldShowReminderField =
        wizard.details.isCommunication !== true &&
        wizard.form.type !== FORM_TYPE.selfService &&
        wizard.form.type !== FORM_TYPE.shareable &&
        wizard.form.type !== FORM_TYPE.application;
    const shouldShowNotificationEmails = wizard.details.isCommunication !== true;

    const selectedTemplateLabel =
        wizard.template.templateObject?.name ||
        wizard.template.templateObject?.formName ||
        '';
    const templateSource =
        wizard.template.creationMethod === 'schoolTemplate'
            ? 'school'
            : 'admicity';

    const isSettingsSectionComplete = () => {
        if (!canShowSettingsSection) {
            return false;
        }

        if (
            shouldShowReminderField &&
            (
                wizard.details.reminderFrequency === '' ||
                wizard.details.reminderFrequency == null
            )
        ) {
            return false;
        }

        const expiry = wizard.details.expiryDate ? moment(wizard.details.expiryDate) : null;
        const goLive = wizard.details.goLiveDate ? moment(wizard.details.goLiveDate) : null;

        return !(expiry && goLive && expiry.isBefore(goLive));
    };

    const isSetupValid = () =>
        isTypeSelected &&
        hasAudienceSelection &&
        isTriggerConfigured &&
        isBuildSectionComplete &&
        formNameValidation.isValid &&
        isSettingsSectionComplete();

    const getResolvedParentAudienceType = () => {
        const mappedParentAudienceType = getParentAudienceTypeForSelection(
            wizard.form.audience,
            wizard.details.parentAccountType
        );

        return (
            wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            mappedParentAudienceType === PARENT_AUDIENCE_TYPE.none &&
            wizard.template.templateObject?.type === 'offer'
                ? PARENT_AUDIENCE_TYPE.applicant
                : mappedParentAudienceType
        );
    };

    const getAssignmentType = () => {
        if (wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers) {
            if (wizard.recipients.assignmentType) {
                return wizard.recipients.assignmentType;
            }

            if (wizard.recipients.employees.length) {
                return FORM_ASSIGNMENT_TYPE.employees;
            }

            return FORM_ASSIGNMENT_TYPE.students;
        }

        switch (wizard.form.audience) {
            case FORM_AUDIENCE_TYPE.employees:
                return FORM_ASSIGNMENT_TYPE.employees;
            case FORM_AUDIENCE_TYPE.students:
            case FORM_AUDIENCE_TYPE.enquiries:
            case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            case FORM_AUDIENCE_TYPE.previousSchool:
                return FORM_ASSIGNMENT_TYPE.students;
            default:
                return FORM_ASSIGNMENT_TYPE.none;
        }
    };

    const buildFormPayload = () => {
        const isCommunicationForm =
            wizard.details.isCommunication ||
            selectedFormType === FORM_TYPE.emailCommunication;
        const audiencePayload = resolveBackendAudiencePayload(
            wizard.form.audience || 0,
            getResolvedParentAudienceType()
        );

        const schema =
            wizard.schema && wizard.schema.length
                ? wizard.schema
                : [
                    ...(wizard.template.templateObject?.schema ?? []),
                    schoolLogoSchemaComponent
                ];

        return {
            templateId: wizard.template.templateId,
            schema,
            name: wizard.details.formName.trim(),
            remindInterval: wizard.details.reminderFrequency || 0,
            categoryIds: wizard.details.categories,
            strictRecipientsFiltering: wizard.details.strictRecipientsFiltering,
            allParentsCanSubmit: wizard.details.allParentsCanSubmit,
            allowMultipleSubmissions: wizard.details.allowMultipleSubmissions,
            enableRecipientNotifications: wizard.details.enableRecipientNotifications,
            audience: audiencePayload.audience,
            parentAudienceType: audiencePayload.parentAudienceType,
            accessLevel: computeAccessLevel(wizard),
            expirationDate: wizard.details.expiryDate || null,
            notificationEmails: wizard.details.notificationEmail,
            assignmentType: getAssignmentType(),
            scheduledActivationAt: wizard.details.goLiveDate || null,
            enableAutoresponder: wizard.details.enableAutoresponder,
            isCommunication: isCommunicationForm,
            ProfileMustBeComplete: wizard.details.profileMustBeComplete,
            linkToStudents: wizard.details.linkToStudents || false,
            allLinkedEmployeesCanSubmit:
                wizard.details.linkToStudents &&
                !!wizard.details.allLinkedEmployeesCanSubmit,
            linkedStrictFiltering: wizard.details.linkedStrictFiltering || false,
        };
    };

    const saveRecipients = async (formId) => {
        const {
            form: {audience},
            details: {linkToStudents},
            recipients: {
                assignmentType,
                student: {
                    students: studentFilters = [],
                    applications: applicationFilters = [],
                    enquiries: enquiryFilters = [],
                } = {},
                employees: employeeFilters = [],
                groups: groupFilters = [],
                externalEmails: externalUserEmailAssignments = [],
            },
            associations: {
                students: linkedStudentFilters = [],
                applications: linkedApplicationFilters = [],
                enquiries: linkedEnquiryFilters = [],
                groups: linkedGroupFilters = [],
            } = {},
        } = wizard;
        const isTriggerBasedForm = wizard.form.type === FORM_TYPE.triggerForm;

        const tasks = [];

        const allStudentRecipients = [
            ...studentFilters,
            ...applicationFilters,
            ...enquiryFilters,
        ].map(item => ({
            personId: item.id,
            name: item.label,
        }));

        const allEmployeeRecipients = employeeFilters.map(item => ({
            personId: item.id,
            name: item.label || `${item.preferredForename ?? ''} ${item.preferredSurname ?? ''}`.trim(),
        }));

        const createdGroupFilters = groupFilters.map(item => ({
            filterId: item.id,
            excluded: !!item.excluded,
        }));

        const externalRecipientEmailAssignmentsForThisAudience =
            wizard.form.type === FORM_TYPE.triggerForm &&
            audience === FORM_AUDIENCE_TYPE.externalUsers
                ? (wizard.trigger.externalEmails ?? [])
                : externalUserEmailAssignments;

        const externalEmails = externalRecipientEmailAssignmentsForThisAudience
            .filter(item => !item.isDeleted)
            .map(item => item.email);

        const shouldAssignStudents =
            !isTriggerBasedForm &&
            (
                [
                    FORM_AUDIENCE_TYPE.students,
                    FORM_AUDIENCE_TYPE.enquiries,
                    FORM_AUDIENCE_TYPE.parentsForChildCompletion,
                    FORM_AUDIENCE_TYPE.previousSchool,
                ].includes(audience) ||
                (
                    audience === FORM_AUDIENCE_TYPE.externalUsers &&
                    assignmentType === FORM_ASSIGNMENT_TYPE.students
                )
            );

        const shouldAssignEmployees =
            !isTriggerBasedForm &&
            (
                audience === FORM_AUDIENCE_TYPE.employees ||
                (
                    audience === FORM_AUDIENCE_TYPE.externalUsers &&
                    assignmentType === FORM_ASSIGNMENT_TYPE.employees
                )
            );

        if (shouldAssignStudents && (allStudentRecipients.length || createdGroupFilters.length)) {
            tasks.push(
                createFormStudentAssignments({
                    formId,
                    personAssignments: allStudentRecipients,
                    groupFilters: createdGroupFilters,
                }).unwrap()
            );
        }

        if (shouldAssignEmployees && (allEmployeeRecipients.length || createdGroupFilters.length)) {
            tasks.push(
                createFormEmployeeAssignment({
                    formId,
                    personAssignments: allEmployeeRecipients,
                    groupFilters: createdGroupFilters,
                }).unwrap()
            );
        }

        if (audience === FORM_AUDIENCE_TYPE.externalUsers && externalEmails.length) {
            tasks.push(
                createExternalUserFormAssignments({
                    formId,
                    emails: externalEmails,
                }).unwrap()
            );
        }

        if (audience === FORM_AUDIENCE_TYPE.employees && linkToStudents === true) {
            const linkedPersonAssignments = [
                ...linkedStudentFilters,
                ...linkedApplicationFilters,
                ...linkedEnquiryFilters,
            ].map(item => ({
                personId: item.id,
                name: item.label,
            }));

            const linkedGroupFiltersPayload = linkedGroupFilters.map(item => ({
                filterId: item.id,
                excluded: !!item.excluded,
            }));

            if (linkedPersonAssignments.length || linkedGroupFiltersPayload.length) {
                tasks.push(
                    createFormStudentAssignments({
                        formId,
                        personAssignments: linkedPersonAssignments,
                        groupFilters: linkedGroupFiltersPayload,
                        linkedStudent: true,
                    }).unwrap()
                );
            }
        }

        if (!tasks.length) {
            return;
        }

        await Promise.all(tasks);
    };

    const createFormAndOpenPostCreateSetup = async () => {
        if (isCreatingForm) {
            return;
        }

        setIsCreatingForm(true);

        try {
            const payload = buildFormPayload();
            let formId = createdFormId;

            if (formId) {
                await updateForm({
                    ...payload,
                    id: formId,
                }).unwrap();
            } else {
                const result = await createForm(payload).unwrap();
                formId = result?.formId || result?.id;

                await saveRecipients(formId);

                if (
                    wizard.trigger?.selectedForm &&
                    wizard.trigger?.relatedComponent &&
                    wizard.trigger?.targetState
                ) {
                    await createFormQuestionAssignment({
                        formId,
                        relatedFormId: wizard.trigger.selectedForm.id,
                        relatedComponentId: wizard.trigger.relatedComponent.id,
                        targetState: `${wizard.trigger.targetState}`,
                        displayName: `${wizard.trigger.selectedForm.name} - ${wizard.trigger.relatedComponent.description}`,
                        ...(wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers &&
                            wizard.trigger.relatedFormEmailFieldId && {
                            relatedFormEmailFieldId: wizard.trigger.relatedFormEmailFieldId
                        }),
                        ...(wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
                            wizard.trigger.relatedFormEmployeeFieldId && {
                            relatedFormEmployeeFieldId: wizard.trigger.relatedFormEmployeeFieldId
                        })
                    }).unwrap();
                }
            }

            showSuccessNotification(
                formId === createdFormId
                    ? 'Form saved successfully'
                    : wizard.details.isCommunication
                        ? 'Email created successfully'
                        : 'Form created successfully'
            );
            setCreatedFormId(formId);
            setStage('postCreate');
        } catch (error) {
            showErrorNotification('Unable to create form');
        } finally {
            setIsCreatingForm(false);
        }
    };

    const handleContinueToForm = async () => {
        if (!isSetupValid() || isCreatingForm) {
            return;
        }

        if (isTemplateSelectionLocked) {
            return;
        }

        setSchemaValidation({});
        setComponentToEditIndex(undefined);
        setStage('builder');
    };

    const isBuilderValid =
        Array.isArray(wizard.schema) &&
        wizard.schema.length > 0 &&
        Object.values(schemaValidation).filter(Boolean).length === 0;
    const builderPrimaryButtonLabel = isCreatingForm
        ? 'Saving...'
        : createdFormId
            ? 'Save & Continue'
            : (wizard.details.isCommunication ? 'Create email' : 'Create form');

    useEffect(() => {
        if (stage !== 'builder') {
            return;
        }

        setIsBuilderAtBottom(false);

        const scrollToTop = () => {
            const container = builderScrollContainerRef.current;

            if (container) {
                container.scrollTo({top: 0, left: 0, behavior: 'auto'});
            }

            window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        };

        requestAnimationFrame(scrollToTop);
    }, [stage]);

    useEffect(() => {
        if (stage !== 'builder') {
            return undefined;
        }

        const sentinel = builderBottomSentinelRef.current;

        if (!sentinel) {
            return undefined;
        }

        const container = builderScrollContainerRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsBuilderAtBottom(entry.isIntersecting);
            },
            {
                root:
                    container && (container.scrollHeight - container.clientHeight) > 1
                        ? container
                        : null,
                rootMargin: '0px 0px 180px 0px',
                threshold: 0.01,
            }
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [stage, wizard.schema]);

    useEffect(() => {
        if (!isTemplateSelectionLocked) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsTemplateSelectionLocked(false);
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [isTemplateSelectionLocked]);

    const renderFloatingBuilderActionButtons = () => (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={2}
            justifyContent={createdFormId ? 'flex-end' : 'space-between'}
            alignItems={{xs: 'stretch', sm: 'center'}}
            sx={{width: '100%'}}
        >
            {!createdFormId && (
                <Button
                    variant="outlined"
                    onClick={() => setStage('setup')}
                    sx={{
                        width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                        ml: {sm: 2},
                        mr: 'auto',
                    }}
                >
                    Back
                </Button>
            )}

            <Button
                variant="contained"
                onClick={createFormAndOpenPostCreateSetup}
                disabled={!isBuilderValid || isCreatingForm}
                sx={{
                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                    px: 1,
                    ml: 'auto',
                    backgroundColor: '#186BBE',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#155FA7',
                        boxShadow: 'none'
                    }
                }}
            >
                {builderPrimaryButtonLabel}
            </Button>
        </Stack>
    );

    const renderAttachedBuilderActionButtons = () => (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={2}
            justifyContent={createdFormId ? 'flex-end' : 'space-between'}
            alignItems={{xs: 'stretch', sm: 'center'}}
            sx={{width: '100%'}}
        >
            {!createdFormId && (
                <Button
                    variant="outlined"
                    onClick={() => setStage('setup')}
                    sx={{
                        width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                        ml: {sm: 2},
                        mr: 'auto',
                    }}
                >
                    Back
                </Button>
            )}

            <Button
                variant="contained"
                onClick={createFormAndOpenPostCreateSetup}
                disabled={!isBuilderValid || isCreatingForm}
                sx={{
                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                    px: 1,
                    ml: 'auto',
                    backgroundColor: '#186BBE',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#155FA7',
                        boxShadow: 'none'
                    }
                }}
            >
                {builderPrimaryButtonLabel}
            </Button>
        </Stack>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                backgroundColor: '#fff',
            }}
        >
            {stage === 'setup'
                ? (
                    <>
                        <Box
                            sx={{
                                p: 3,
                                borderBottom: '1px solid #e0e0e0',
                                backgroundColor: '#fff',
                            }}
                        >
                            <Typography variant="h5" sx={{fontWeight: 600}}>
                                Create Form
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                Complete each section in order. Later sections only appear after the required earlier choices are made.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                py: 3,
                                px: {
                                    xs: 3,
                                    md: SETTINGS_SIDE_CLEARANCE,
                                }
                            }}
                        >
                            <Stack
                                spacing={3}
                                sx={{
                                    width: '100%',
                                    maxWidth: SETTINGS_CONTENT_MAX_WIDTH,
                                    mx: 'auto'
                                }}
                            >
                                <ProgressiveSection
                                    question="What type of form is this?"
                                    header="Form Type"
                                    headerIcon={<AutoAwesomeMotionOutlinedIcon fontSize="small"/>}
                                    subHeader="Choose how this form should behave."
                                >
                                    <TextField
                                        select
                                        fullWidth
                                        label="Form Type"
                                        value={selectedFormType}
                                        onChange={handleFormTypeChange}
                                    >
                                        {CREATE_FORM_TYPE_OPTIONS.map(option => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {option.description}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </ProgressiveSection>

                                {isTypeSelected && requiresAudience && (
                                    <ProgressiveSection
                                        question={
                                            wizard.details.isCommunication
                                                ? 'Who will receive this email?'
                                                : 'Who will receive and complete this form?'
                                        }
                                        header="Audience"
                                        headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                                        subHeader={
                                            wizard.details.isCommunication
                                                ? 'Choose who will receive this email.'
                                                : 'Choose who will receive and complete this form.'
                                        }
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            label="Audience"
                                            value={wizard.form.selection || ''}
                                            onChange={handleAudienceChange}
                                        >
                                            {audienceOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </ProgressiveSection>
                                )}

                    {isTypeSelected && hasAudienceSelection && isTriggerForm && (
                        <ProgressiveSection
                            question="Which form response should trigger this form?"
                            header="Trigger Settings"
                            headerIcon={<SettingsOutlinedIcon fontSize="small"/>}
                        >
                            <StepTriggerConditions
                                wizard={wizard}
                                setWizard={setWizard}
                            />
                        </ProgressiveSection>
                    )}

                    {isTypeSelected && hasAudienceSelection && !isTriggerForm && showRecipientSection && (
                        <ProgressiveSection
                            question="How should recipients be selected?"
                            header="Recipient Settings"
                            headerIcon={<SettingsOutlinedIcon fontSize="small"/>}
                        >
                            <StepRecipientAssociation
                                wizard={wizard}
                                setWizard={setWizard}
                                wizardAccessLevel={accessLevel}
                            />
                        </ProgressiveSection>
                    )}

                    {isTypeSelected && hasAudienceSelection && !isTriggerForm && showExternalRecipients && (
                        <ProgressiveSection
                            question="Which external email addresses should be included?"
                            header="External User Recipients"
                            headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                        >
                            <StepExternalRecipients
                                wizard={wizard}
                                setWizard={setWizard}
                            />
                        </ProgressiveSection>
                    )}

                    {isTypeSelected && hasAudienceSelection && !isTriggerForm && showLinkedStudents && (
                        <ProgressiveSection
                            question="Which students should this employee form be linked to?"
                            header="Linked Students"
                            headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                        >
                            <StepEmployeeStudentAssociation
                                wizard={wizard}
                                setWizard={setWizard}
                                wizardAccessLevel={accessLevel}
                            />
                        </ProgressiveSection>
                    )}

                    {isTypeSelected && hasAudienceSelection && showParentCompletion && (
                        <ProgressiveSection
                            question="Do you want parent completion rules on this form?"
                            header="Parent Completion"
                            headerIcon={<SupervisorAccountIcon fontSize="small"/>}
                        >
                            <Stack spacing={0}>
                                <StepRecipientBothParents
                                    wizard={wizard}
                                    setWizard={setWizard}
                                />
                                <StepCompletedProfiles
                                    wizard={wizard}
                                    setWizard={setWizard}
                                />
                            </Stack>
                        </ProgressiveSection>
                    )}

                    {canShowTemplateSection && (
                        <ProgressiveSection
                            question="How would you like to start building this form?"
                            header="Template Selection"
                            headerIcon={<AutoAwesomeMotionOutlinedIcon fontSize="small"/>}
                            subHeader="Choose an Admicity, blank, or school template."
                        >
                            <Stack spacing={2.5}>
                                <TextField
                                    select
                                    fullWidth
                                    value={wizard.template.creationMethod}
                                    onChange={handleBuildMethodChange}
                                >
                                    <MenuItem value="template">Use an Admicity template</MenuItem>
                                    <MenuItem value="schoolTemplate">Use a school template</MenuItem>
                                </TextField>

                                {!!wizard.template.creationMethod && (
                                    <Stack
                                        direction={{xs: 'column', sm: 'row'}}
                                        spacing={1.5}
                                        alignItems={{xs: 'flex-start', sm: 'center'}}
                                        justifyContent="space-between"
                                    >
                                        {isTemplateMode && (
                                            <Button
                                                variant={selectedTemplateLabel ? 'outlined' : 'contained'}
                                                onClick={() => setIsTemplateDialogOpen(true)}
                                                sx={{
                                                    boxShadow: 'none',
                                                    backgroundColor: selectedTemplateLabel ? undefined : '#186BBE',
                                                    '&:hover': {
                                                        boxShadow: 'none',
                                                        backgroundColor: selectedTemplateLabel ? undefined : '#155FA7',
                                                    }
                                                }}
                                            >
                                                {selectedTemplateLabel ? 'Change template' : 'Choose template'}
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </ProgressiveSection>
                    )}

                    {canShowFormDetailsSection && (
                        <ProgressiveSection
                            question={
                                wizard.details.isCommunication
                                    ? 'What should this email be called?'
                                    : 'What should this form be called?'
                            }
                            header={wizard.details.isCommunication ? 'Email Details' : 'Form Details'}
                            headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}
                        >
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label={wizard.details.isCommunication ? 'Email Name' : 'Form Name'}
                                    autoComplete="off"
                                    required
                                    value={wizard.details.formName}
                                    error={!formNameValidation.isValid}
                                    helperText={formNameValidation.message || ' '}
                                    inputProps={{maxLength: FORM_NAME_MAX_LENGTH}}
                                    onChange={(event) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            details: {
                                                ...prev.details,
                                                formName: event.target.value,
                                            }
                                        }))
                                    }
                                />

                                <TextField
                                    select
                                    fullWidth
                                    value={wizard.details.categories}
                                    label="Categories (optional)"
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                            if (!formCategories?.length) {
                                                return <em>No categories</em>;
                                            }

                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return <em>No categories</em>;
                                            }

                                            const selectedCats = formCategories.filter(category =>
                                                selected.includes(category.id)
                                            );

                                            return (
                                                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                                    {selectedCats.map(category => (
                                                        <Box
                                                            key={category.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.8,
                                                                padding: 1,
                                                                borderRadius: '18px',
                                                                border: '1px solid #adadad',
                                                            }}
                                                        >
                                                            <CircleIcon
                                                                fontSize="small"
                                                                sx={{color: category.color}}
                                                            />
                                                            <Typography variant="body2">
                                                                {category.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            );
                                        },
                                        MenuProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': {
                                                    whiteSpace: 'unset',
                                                    wordBreak: 'break-all'
                                                }
                                            }
                                        }
                                    }}
                                    onChange={(event) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            details: {
                                                ...prev.details,
                                                categories: event.target.value,
                                            }
                                        }))
                                    }
                                >
                                    {!formCategories?.length && (
                                        <MenuItem disabled>
                                            <em>No categories</em>
                                        </MenuItem>
                                    )}
                                    {formCategories.map(category => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Checkbox checked={wizard.details.categories.includes(category.id)}/>
                                            <CircleIcon
                                                fontSize="small"
                                                sx={{
                                                    color: category.color,
                                                    mr: 1
                                                }}
                                            />
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </ProgressiveSection>
                    )}

                                {canShowSettingsSection && (
                                    <ProgressiveSection
                                        question="What settings should apply to this form?"
                                        header="Settings"
                                        headerIcon={<SettingsOutlinedIcon fontSize="small"/>}
                                    >
                                        <Stack spacing={3}>
                                            {shouldShowExpirationDate && (
                                                <FormExpirationDatePicker
                                                    label="Expiration date (optional)"
                                                    expirationDate={wizard.details.expiryDate || null}
                                                    onExpirationDateChange={(expiryDate) =>
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                expiryDate,
                                                            },
                                                        }))
                                                    }
                                                />
                                            )}

                                            {shouldShowReminderField && (
                                                <TextField
                                                    fullWidth
                                                    label="Remind interval (optional)"
                                                    variant="outlined"
                                                    value={wizard.details.reminderFrequency}
                                                    onChange={(event) =>
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                reminderFrequency: event.target.value.replace(/\D/g, ''),
                                                            }
                                                        }))
                                                    }
                                                    helperText="In hours (0 = Disabled)"
                                                    inputProps={{
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]*'
                                                    }}
                                                />
                                            )}

                                            {shouldShowNotificationEmails && (
                                                <EmailsInput
                                                    label="Email address to be notified when this form receives submissions (optional)"
                                                    values={wizard.details.notificationEmail.map(email => ({email}))}
                                                    onChange={(emails) =>
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                notificationEmail: Array.from(
                                                                    new Map(
                                                                        (emails || []).map(item => [item.email, item.email])
                                                                    ).values()
                                                                )
                                                            }
                                                        }))
                                                    }
                                                    onBlur={(email) => {
                                                        if (!email) {
                                                            return;
                                                        }

                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                notificationEmail: Array.from(
                                                                    new Map([
                                                                        ...prev.details.notificationEmail.map(item => [item, item]),
                                                                        [email, email]
                                                                    ]).values()
                                                                )
                                                            }
                                                        }));
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </ProgressiveSection>
                                )}
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#fff',
                                px: 3,
                                py: 2,
                            }}
                        >
                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                spacing={2}
                                justifyContent="flex-end"
                                alignItems={{xs: 'stretch', sm: 'center'}}
                                sx={{
                                    width: '100%',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleContinueToForm}
                                    disabled={
                                        !isSetupValid() ||
                                        isCreatingForm ||
                                        isTemplateSelectionLocked
                                    }
                                    sx={{
                                        ml: {sm: 'auto'},
                                        mr: {sm: 4},
                                        backgroundColor: '#186BBE',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#155FA7',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    {wizard.details.isCommunication ? 'Continue to email' : 'Continue to form'}
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )
                : stage === 'builder'
                    ? (
                        <>
                            <Box
                                sx={{
                                    p: 3,
                                    borderBottom: '1px solid #e0e0e0',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Typography variant="h5" sx={{fontWeight: 600}}>
                                    Create Form
                                </Typography>
                                {wizard.details.formName && (
                                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                        {wizard.details.formName}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{flex: 1, minHeight: 0}}>
                                <StepFormEditor
                                    wizard={wizard}
                                    setWizard={setWizard}
                                    formId={createdFormId}
                                    accessLevel={accessLevel}
                                    schema={wizard.schema}
                                    onSchemaChange={(nextSchema) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            schema: nextSchema,
                                        }))
                                    }
                                    componentToEditIndex={componentToEditIndex}
                                    setComponentToEditIndex={setComponentToEditIndex}
                                    onSchemaValidationChange={setSchemaValidation}
                                    scrollContainerRef={builderScrollContainerRef}
                                    onScrollContainerScroll={undefined}
                                />
                            </Box>

                            <Box
                                ref={builderBottomSentinelRef}
                                sx={{
                                    height: 1,
                                    width: '100%',
                                }}
                            />

                            {isBuilderAtBottom && (
                                <Box
                                    sx={{
                                        borderTop: '1px solid #e0e0e0',
                                        backgroundColor: '#fff',
                                        px: 2,
                                        py: 2,
                                    }}
                                >
                                    {renderAttachedBuilderActionButtons()}
                                </Box>
                            )}
                        </>
                    )
                    : (
                        <FormPostCreateSetup
                            formIdOverride={createdFormId}
                            onBackToBuilder={() => setStage('builder')}
                        />
                )}

            {stage === 'builder' && !isBuilderAtBottom && !isSmallScreen && (
                <Box
                        sx={(theme) => ({
                            position: 'fixed',
                            bottom: theme.spacing(2),
                            left: theme.spacing(2),
                            right: theme.spacing(2),
                            zIndex: theme.zIndex.drawer + 4,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            pointerEvents: 'none',
                        })}
                    >
                    <Box
                        sx={{
                            width: '100%',
                            px: {xs: 1, lg: 1.5, xl: 2},
                            pointerEvents: 'auto',
                        }}
                    >
                        {renderFloatingBuilderActionButtons()}
                    </Box>
                </Box>
            )}

            <Dialog
                open={isTemplateDialogOpen}
                onClose={() => setIsTemplateDialogOpen(false)}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle sx={{pr: 6}}>
                    Choose a Template
                    <IconButton
                        onClick={() => setIsTemplateDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                        }}
                    >
                        <CancelIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{p: 0}}>
                    <StepTemplateSelection
                        wizard={wizard}
                        setWizard={setWizard}
                        templateSource={templateSource}
                        onBack={() => setIsTemplateDialogOpen(false)}
                        onSelected={() => {
                            setIsTemplateSelectionLocked(true);
                            setIsTemplateDialogOpen(false);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CreateFormWizard;
